import type { Activity, FitMessage } from '@fit-editor/fit-core'

import type { FieldMeta, FitProfileIndex, MessageMeta } from '@/lib/fit-profile'
import { rawFromScaled } from '@/lib/fit-profile'

/**
 * Pure, presentation-only helpers behind the "FIT data" inspector view:
 * grouping an `Activity` back into FIT message types, describing each
 * message instance's fields (profile-defined / undocumented / developer),
 * and discovering which columns a table of messages actually has data
 * for. Nothing here mutates or recomputes a domain value.
 */

const UNKNOWN_KEY_PATTERN = /^\d+$/

/** The `Activity` properties promoted out of the SDK's `messages` dictionary, mapped back to their `messagesKey`. */
const NAMED_GROUPS = [
  ['fileId', 'fileIdMesgs'],
  ['activitySummary', 'activityMesgs'],
  ['sessions', 'sessionMesgs'],
  ['laps', 'lapMesgs'],
  ['events', 'eventMesgs'],
  ['records', 'recordMesgs'],
] as const

/** FIT base type ids (already masked with `0x1F`, see `raw-fit.ts`'s `FitFieldDef`). */
const BASE_TYPE_NAMES: Record<number, string> = {
  0: 'enum',
  1: 'sint8',
  2: 'uint8',
  3: 'sint16',
  4: 'uint16',
  5: 'sint32',
  6: 'uint32',
  7: 'string',
  8: 'float32',
  9: 'float64',
  10: 'uint8z',
  11: 'uint16z',
  12: 'uint32z',
  13: 'byte',
  14: 'sint64',
  15: 'uint64',
  16: 'uint64z',
}

export function baseTypeName(baseType: number): string {
  return BASE_TYPE_NAMES[baseType & 0x1f] ?? `0x${baseType.toString(16)}`
}

/** `deviceInfo` -> `device_info` (the FIT SDK documentation's naming). */
export function toSnakeCase(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()
}

/** `totalElapsedTime` -> `Total elapsed time`. */
export function humanizeFieldName(name: string): string {
  const spaced = toSnakeCase(name).replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export interface MessageGroup {
  /** Stable id: the SDK `messagesKey`, or `unknown:<num>` for a preserved unknown type. */
  id: string
  /** FIT message name in snake_case (`device_info`), or `unknown_<num>`. */
  name: string
  /** Global message number, when known. */
  mesgNum: number | undefined
  /** The SDK `messagesKey`, absent for preserved unknown message types. */
  messagesKey: string | undefined
  count: number
  /** True for a message type the FIT Profile doesn't define (preserved verbatim by the raw-fit layer). */
  unknownType: boolean
}

function messagesKeyToFallbackName(messagesKey: string): string {
  return toSnakeCase(messagesKey.replace(/Mesgs$/, ''))
}

/** Every message instance list in the activity keyed by SDK `messagesKey` (named + other). */
export function messagesByKey(activity: Activity): Map<string, FitMessage[]> {
  const out = new Map<string, FitMessage[]>()
  for (const [prop, key] of NAMED_GROUPS) {
    const value = activity[prop]
    if (value == null) continue
    const list = Array.isArray(value) ? (value as FitMessage[]) : [value as FitMessage]
    if (list.length > 0) out.set(key, list)
  }
  for (const [key, list] of Object.entries(activity.otherMessages)) {
    if (list.length > 0) out.set(key, list)
  }
  return out
}

/** One group per message type present, known types first (by message number), then preserved unknown types. */
export function listMessageGroups(
  activity: Activity,
  profile: FitProfileIndex | null,
): MessageGroup[] {
  const groups: MessageGroup[] = []
  for (const [key, list] of messagesByKey(activity)) {
    const meta = profile?.byMessagesKey.get(key)
    groups.push({
      id: key,
      name: meta != null ? toSnakeCase(meta.name) : messagesKeyToFallbackName(key),
      mesgNum: meta?.num,
      messagesKey: key,
      count: list.length,
      unknownType: false,
    })
  }
  groups.sort((a, b) => (a.mesgNum ?? 1e9) - (b.mesgNum ?? 1e9) || a.name.localeCompare(b.name))

  const unknownCounts = new Map<number, number>()
  for (const m of activity.preservedUnknownMessages) {
    unknownCounts.set(m.globalMessageNumber, (unknownCounts.get(m.globalMessageNumber) ?? 0) + 1)
  }
  for (const [num, count] of [...unknownCounts].sort((a, b) => a[0] - b[0])) {
    groups.push({
      id: `unknown:${num}`,
      name: `unknown_${num}`,
      mesgNum: num,
      messagesKey: undefined,
      count,
      unknownType: true,
    })
  }
  return groups
}

export interface DeveloperFieldInfo {
  key: number
  name: string
  units: string
  developerDataIndex: number | undefined
  fieldDefinitionNumber: number | undefined
}

function firstString(value: unknown): string {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    const s = value.find((v) => typeof v === 'string' && v.length > 0)
    return typeof s === 'string' ? s : ''
  }
  return ''
}

/**
 * Developer field descriptions keyed by the SDK's `field_description.key`
 * -- the same key the decoder uses for each message's `developerFields`
 * object (see `@garmin/fitsdk`'s `Decoder#decodeMessage`).
 */
export function developerFieldIndex(activity: Activity): Map<number, DeveloperFieldInfo> {
  const out = new Map<number, DeveloperFieldInfo>()
  for (const fd of activity.otherMessages.fieldDescriptionMesgs ?? []) {
    const key = typeof fd.key === 'number' ? fd.key : undefined
    if (key == null) continue
    const fieldDefinitionNumber =
      typeof fd.fieldDefinitionNumber === 'number' ? fd.fieldDefinitionNumber : undefined
    out.set(key, {
      key,
      name: firstString(fd.fieldName) || `developer_field_${fieldDefinitionNumber ?? key}`,
      units: firstString(fd.units),
      developerDataIndex:
        typeof fd.developerDataIndex === 'number' ? fd.developerDataIndex : undefined,
      fieldDefinitionNumber,
    })
  }
  return out
}

export type FieldKind = 'profile' | 'undocumented' | 'developer'

export interface FieldRow {
  /** Unique within the message: the decoded object key, or `dev:<key>` for a developer field. */
  id: string
  /** Display name (snake_case FIT name, `field_<num>` for undocumented, developer field name). */
  name: string
  kind: FieldKind
  /** Field definition number when known. */
  num: number | undefined
  value: unknown
  units: string
  /** The raw on-the-wire value, when it differs from `value` (scaled fields). */
  raw: unknown
  /** FIT base type name, when known from the preserved field definition. */
  baseType: string | undefined
  meta: FieldMeta | undefined
  hasData: boolean
}

/** Seconds between the Unix epoch and the FIT epoch (1989-12-31T00:00:00Z). */
const FIT_EPOCH_OFFSET_S = 631065600

/** The raw FIT `date_time` (seconds since the FIT epoch) a decoded `Date` came from. */
export function dateToFitTimestamp(date: Date): number | undefined {
  const ms = date.getTime()
  if (!Number.isFinite(ms)) return undefined
  return Math.round(ms / 1000) - FIT_EPOCH_OFFSET_S
}

export function hasData(value: unknown): boolean {
  if (value == null) return false
  if (typeof value === 'string') return value.length > 0
  if (Array.isArray(value)) return value.some((v) => v != null)
  return true
}

/** Index of `mixedMessageUnknownFields`' base-type info: `messagesKey#index` -> field num -> base type name. */
export function unknownFieldBaseTypes(activity: Activity): Map<string, Map<number, string>> {
  const out = new Map<string, Map<number, string>>()
  for (const entry of activity.mixedMessageUnknownFields) {
    const byNum = new Map<number, string>()
    for (const f of entry.unknownFields) {
      byNum.set(f.def.fieldDefinitionNumber, baseTypeName(f.def.baseType))
    }
    out.set(`${entry.messagesKey}#${entry.index}`, byNum)
  }
  return out
}

export interface DescribeOptions {
  messageMeta: MessageMeta | undefined
  devFields: Map<number, DeveloperFieldInfo>
  /** Base types for this instance's undocumented fields (from `unknownFieldBaseTypes`). */
  unknownBaseTypes?: Map<number, string> | undefined
  /** Also list profile fields this instance doesn't carry (value `undefined`). */
  includeEmpty?: boolean | undefined
}

/** Describes every field on one decoded message instance. */
export function describeMessageFields(message: FitMessage, opts: DescribeOptions): FieldRow[] {
  const rows: FieldRow[] = []
  const seen = new Set<string>()
  for (const [key, value] of Object.entries(message)) {
    if (key === 'developerFields') continue
    seen.add(key)
    if (UNKNOWN_KEY_PATTERN.test(key)) {
      const num = Number(key)
      rows.push({
        id: key,
        name: `field_${num}`,
        kind: 'undocumented',
        num,
        value,
        units: '',
        raw: undefined,
        baseType: opts.unknownBaseTypes?.get(num),
        meta: undefined,
        hasData: hasData(value),
      })
      continue
    }
    const meta = opts.messageMeta?.fieldsByName.get(key)
    rows.push({
      id: key,
      name: toSnakeCase(key),
      kind: 'profile',
      num: meta?.num,
      value,
      units: meta?.units ?? '',
      raw: value instanceof Date ? dateToFitTimestamp(value) : rawFromScaled(value, meta),
      baseType: undefined,
      meta,
      hasData: hasData(value),
    })
  }

  if (opts.includeEmpty && opts.messageMeta != null) {
    for (const meta of opts.messageMeta.fieldsByNum.values()) {
      if (seen.has(meta.name)) continue
      rows.push({
        id: meta.name,
        name: toSnakeCase(meta.name),
        kind: 'profile',
        num: meta.num,
        value: undefined,
        units: meta.units,
        raw: undefined,
        baseType: undefined,
        meta,
        hasData: false,
      })
    }
  }

  const dev = message.developerFields
  if (dev != null && typeof dev === 'object') {
    for (const [key, value] of Object.entries(dev as Record<string, unknown>)) {
      const info = opts.devFields.get(Number(key))
      rows.push({
        id: `dev:${key}`,
        name: info?.name ?? `developer_field_${key}`,
        kind: 'developer',
        num: info?.fieldDefinitionNumber,
        value,
        units: info?.units ?? '',
        raw: undefined,
        baseType: undefined,
        meta: undefined,
        hasData: hasData(value),
      })
    }
  }
  return rows
}

export interface ColumnDef {
  /** Decoded object key, or `dev:<key>` for a developer field. */
  id: string
  label: string
  units: string
  kind: FieldKind
  meta: FieldMeta | undefined
}

/**
 * Columns with data across `messages`, in `preferredOrder` first and then
 * the rest alphabetically (undocumented numeric keys and developer fields
 * last). One linear pass over every instance -- callers memoize per
 * activity.
 */
export function discoverColumns(
  messages: readonly FitMessage[],
  opts: {
    messageMeta: MessageMeta | undefined
    devFields: Map<number, DeveloperFieldInfo>
    preferredOrder?: readonly string[] | undefined
    exclude?: readonly string[] | undefined
  },
): ColumnDef[] {
  const keys = new Set<string>()
  const devKeys = new Set<string>()
  for (const m of messages) {
    for (const key in m) {
      if (key === 'developerFields') {
        const dev = m.developerFields
        if (dev != null && typeof dev === 'object') {
          for (const dk in dev as Record<string, unknown>) {
            if (hasData((dev as Record<string, unknown>)[dk])) devKeys.add(dk)
          }
        }
        continue
      }
      if (!keys.has(key) && hasData(m[key])) keys.add(key)
    }
  }
  const exclude = new Set(opts.exclude ?? [])
  const preferred = (opts.preferredOrder ?? []).filter((k) => keys.has(k) && !exclude.has(k))
  const preferredSet = new Set(preferred)
  const rest = [...keys]
    .filter((k) => !preferredSet.has(k) && !exclude.has(k))
    .sort((a, b) => {
      const an = UNKNOWN_KEY_PATTERN.test(a)
      const bn = UNKNOWN_KEY_PATTERN.test(b)
      if (an !== bn) return an ? 1 : -1
      return an ? Number(a) - Number(b) : a.localeCompare(b)
    })

  const columns: ColumnDef[] = [...preferred, ...rest].map((key) => {
    if (UNKNOWN_KEY_PATTERN.test(key)) {
      return { id: key, label: `field_${key}`, units: '', kind: 'undocumented', meta: undefined }
    }
    const meta = opts.messageMeta?.fieldsByName.get(key)
    return {
      id: key,
      label: humanizeFieldName(key),
      units: meta?.units ?? '',
      kind: 'profile',
      meta,
    }
  })
  for (const dk of [...devKeys].sort((a, b) => Number(a) - Number(b))) {
    const info = opts.devFields.get(Number(dk))
    columns.push({
      id: `dev:${dk}`,
      label: info?.name ?? `developer_field_${dk}`,
      units: info?.units ?? '',
      kind: 'developer',
      meta: undefined,
    })
  }
  return columns
}

/** Reads a column's value off a message (handles `dev:<key>` developer columns). */
export function readColumn(message: FitMessage, columnId: string): unknown {
  if (columnId.startsWith('dev:')) {
    const dev = message.developerFields
    if (dev == null || typeof dev !== 'object') return undefined
    return (dev as Record<string, unknown>)[columnId.slice(4)]
  }
  return message[columnId]
}

/** Lowercased searchable text for a field row: name, units, and its formatted value. */
export function fieldMatches(row: FieldRow, query: string, formattedValue: string): boolean {
  if (query.length === 0) return true
  return (
    row.name.toLowerCase().includes(query) ||
    row.id.toLowerCase().includes(query) ||
    formattedValue.toLowerCase().includes(query)
  )
}
